import { isValid, parse } from 'date-fns';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useControllableState } from '../../utils/useControllableState.tsx';
import Field, { FieldProps } from './Field.tsx';

export const TIME_VALIDATION_REGEXP =
    /^(?:[0-2]|[0-1][0-9]|2[0-3]|[0-1][0-9]:|2[0-3]:|[0-1][0-9]:[0-5]|2[0-3]:[0-5]|[0-1][0-9]:[0-5][0-9]|2[0-3]:[0-5][0-9])$/;

export interface TimeFieldProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue'>,
        FieldProps {}

const TimeField = ({
    ref,
    defaultValue,
    value: propsValue,
    onValueChange,
    onChange,
    onBlur,
    onCommit,
    readOnly,
    disabled,
    className,
    controls,
    placeholder,
    inputRef = undefined,
    ...props
}: TimeFieldProps) => {
    /*
     * Field to enforce HH:mm format
     */

    const [value, setValue] = useControllableState(defaultValue, propsValue, onValueChange);
    const [inputValue, setInputValue] = useState<string>(defaultValue);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const handleBlur = (e: any) => {
        const parsedTime = parse(e.target.value, 'HH:mm', new Date());
        // rather empty than invalid
        const nextValue = isValid(parsedTime) ? e.target.value : '';
        setValue(nextValue);
        onBlur?.(e);
        onCommit?.(nextValue);
    };

    const handleChange = (e: any) => {
        if (e.target.value.length > 5) return;

        if (e.target.value && !TIME_VALIDATION_REGEXP.test(e.target.value)) {
            e.preventDefault();
            return;
        }

        if (e.target.value.length == 2 && inputValue.length !== 3) {
            // If a second number is being added, automatically add a colon
            setInputValue(e.target.value + ':');
        } else if (e.target.value.length == 2 && inputValue.length === 3) {
            // in case the colon is deleted, remove it
            setInputValue(e.target.value.substring(0, 1));
        } else {
            // Otherwise set it as is
            setInputValue(e.target.value);
        }
    };

    return (
        <Field className={className} readOnly={readOnly} disabled={disabled} ref={ref}>
            <Field.Input>
                <input
                    type="text"
                    value={inputValue || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    readOnly={readOnly}
                    disabled={disabled}
                    className="px-2 py-1.5 flex-1 min-w-0 bg-surface-transparent tabular-nums outline-none"
                    placeholder={placeholder ? `${placeholder} (HH:mm)` : 'HH:mm'}
                    {...props}
                    ref={inputRef}
                />
            </Field.Input>
            <Field.Controls>{controls}</Field.Controls>
        </Field>
    );
};

export default TimeField;
